/**
 * Promote footnote citations from in-page jumps into direct source links.
 *
 * `remark-gfm` renders `[^1]` as `<sup><a data-footnote-ref>1</a></sup>` plus a definition
 * list at the end of the message. Every citation Cara emits is a definition whose body is
 * a bare URL, so we copy that URL onto the marker and the reader reaches the source in one
 * click.
 *
 * This runs inside the markdown pipeline rather than as a DOM mutation so each render
 * re-derives the href from the current content. A definition that is still streaming in
 * therefore cannot be captured in a half-written state and frozen onto the marker.
 */

type HastNode = {
  type: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  value?: string;
  children?: HastNode[];
};

/** The statics routes a citation points at, and the identifier shape they carry. */
const CITATION_ROUTE_RE = /^\/agency\/(?:doc|files|kb)\//;
const CITATION_PATH_RE = /^\/agency\/(?:doc|files|kb)\/[a-z]+_[A-Za-z0-9_]{8,}$/;

/** A citation path an earlier iteration emitted; statics serves `/agency/doc/` instead. */
const RETIRED_FILE_PATH_RE = /^\/agency\/files\//;

const URL_IN_TEXT_RE = /https?:\/\/\S+/;

/** Sentence punctuation a markdown renderer leaves attached to the end of a bare URL. */
const TRAILING_PUNCT_RE = /[.,;:!?'")\]]+$/;

/**
 * Extract the citation href from a footnote definition's plain text, or return null when
 * the definition cannot resolve — it is prose, or a URL with no document to open.
 *
 * Reading the text rather than the anchor `remark-gfm` produced keeps us out of the way of
 * GFM's autolink-literal rules, which drop trailing punctuation from the link but leave it
 * in the text.
 */
export const resolveCitationHref = (definitionText: string): string | null => {
  const match = definitionText.match(URL_IN_TEXT_RE);
  if (!match) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(match[0].replace(TRAILING_PUNCT_RE, ''));
  } catch {
    return null;
  }

  // A bare host carries no document — what a citation truncated mid-host looks like. The
  // definition list stays visible in that case, so the URL is still reachable there.
  if (parsed.pathname === '/') {
    return null;
  }

  // A citation route whose identifier is malformed cannot resolve either. Other statics
  // routes and third-party URLs are taken as given.
  if (CITATION_ROUTE_RE.test(parsed.pathname) && !CITATION_PATH_RE.test(parsed.pathname)) {
    return null;
  }

  parsed.pathname = parsed.pathname.replace(RETIRED_FILE_PATH_RE, '/agency/doc/');
  return parsed.toString();
};

const textContent = (node: HastNode): string =>
  node.type === 'text' ? (node.value ?? '') : (node.children ?? []).map(textContent).join('');

/**
 * Walk every node in a hast tree. Hand-rolled rather than using `unist-util-visit`, which
 * would add a dependency for six lines.
 */
const walk = (node: HastNode, visit: (n: HastNode) => void) => {
  visit(node);
  for (const child of node.children ?? []) {
    walk(child, visit);
  }
};

/**
 * Rehype plugin that resolves citation footnotes. Runs after `rehype-sanitize`, so
 * definition ids already carry the clobber prefix and the `target`/`rel` it sets survive.
 */
export const rehypeFootnoteCitations = () => (tree: HastNode) => {
  const sections: HastNode[] = [];
  const markers: HastNode[] = [];

  walk(tree, (node) => {
    if (!node.properties) {
      return;
    }
    if (node.tagName === 'section' && 'dataFootnotes' in node.properties) {
      sections.push(node);
    } else if (node.tagName === 'a' && 'dataFootnoteRef' in node.properties) {
      markers.push(node);
    }
  });

  // Definition text includes the trailing `↩` backref, which is always separated by a
  // space and so cannot extend the URL match.
  const definitions = new Map<string, string | null>();
  for (const section of sections) {
    walk(section, (node) => {
      const id = node.properties?.id;
      if (node.tagName === 'li' && typeof id === 'string' && id) {
        definitions.set(id, resolveCitationHref(textContent(node)));
      }
    });
  }

  let references = 0;
  let linkedToSource = 0;

  for (const marker of markers) {
    const properties = marker.properties!;
    const href = properties.href;
    if (typeof href !== 'string' || !href.startsWith('#')) {
      continue;
    }

    // `remark-rehype` writes `#user-content-fn-1` while `rehype-sanitize` clobbers the
    // definition's id to `user-content-user-content-fn-1`, so the prefix goes on once more.
    // Resolving through the href rather than the badge number also handles SEARCH_FILE's
    // non-numeric labels (`src-1a2b3c4d`), which markdown still renders as plain numbers.
    const definitionId = `user-content-${href.slice(1)}`;
    if (!definitions.has(definitionId)) {
      continue;
    }

    references++;
    const resolved = definitions.get(definitionId);
    if (resolved) {
      properties.href = resolved;
      properties.target = '_blank';
      properties.rel = ['noopener', 'noreferrer'];
      linkedToSource++;
    } else {
      // Nothing to link out to, so point the marker at its own definition rather than at
      // the generated href, which misses sanitize's second prefix and matches nothing.
      properties.href = `#${definitionId}`;
    }
  }

  // The definition list is the only place a prose citation's document and excerpt appear,
  // so it stays visible unless every marker now links out.
  if (references > 0 && references === linkedToSource) {
    for (const section of sections) {
      const className = section.properties!.className;
      const classes = Array.isArray(className) ? className.map(String) : [];
      section.properties!.className = [...classes, 'sr-only'];
    }
  }
};
